import React, { useState, useEffect } from 'react';
import { useOnboarding } from './OnboardingContext';
import { X, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * OnboardingTooltip - Shows a tooltip highlighting a specific feature
 * Automatically marks the related substep as complete when dismissed
 */
const OnboardingTooltip = ({ 
  stepId, 
  substepId, 
  title, 
  description, 
  position = 'bottom', // top, bottom, left, right
  children,
  showArrow = true,
  autoComplete = true
}) => {
  const { showChecklist, isSubstepCompleted, completeSubstep } = useOnboarding();
  const [dismissed, setDismissed] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const isCompleted = isSubstepCompleted(stepId, substepId);

  useEffect(() => {
    // Show tooltip after a short delay if not completed
    if (!isCompleted && showChecklist && !dismissed) {
      const timer = setTimeout(() => setIsVisible(true), 500);
      return () => clearTimeout(timer);
    }
  }, [isCompleted, showChecklist, dismissed]);

  const handleDismiss = async () => {
    setDismissed(true);
    setIsVisible(false);
  };

  const handleComplete = async () => {
    await completeSubstep(stepId, substepId);
    setDismissed(true);
    setIsVisible(false);
  };

  if (!showChecklist || isCompleted || dismissed || !isVisible) {
    return children;
  }

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2'
  };

  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 border-t-emerald-600 border-l-transparent border-r-transparent border-b-transparent',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-emerald-600 border-l-transparent border-r-transparent border-t-transparent',
    left: 'left-full top-1/2 -translate-y-1/2 border-l-emerald-600 border-t-transparent border-b-transparent border-r-transparent',
    right: 'right-full top-1/2 -translate-y-1/2 border-r-emerald-600 border-t-transparent border-b-transparent border-l-transparent'
  };

  return (
    <div className="relative inline-block">
      {/* Highlight ring around children */}
      <div className="relative">
        <div className="absolute -inset-1 bg-emerald-500/20 rounded-lg animate-pulse" />
        <div className="relative">{children}</div>
      </div>

      {/* Tooltip */}
      <div className={`absolute z-50 ${positionClasses[position]}`}>
        <div className="bg-emerald-600 text-white rounded-lg shadow-lg p-3 w-64 animate-in fade-in slide-in-from-bottom-2">
          {/* Arrow */}
          {showArrow && (
            <div className={`absolute w-0 h-0 border-8 ${arrowClasses[position]}`} />
          )}
          
          <div className="flex items-start justify-between gap-2 mb-2">
            <h4 className="font-semibold text-sm">{title}</h4>
            <button
              onClick={handleDismiss}
              className="text-emerald-200 hover:text-white flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <p className="text-xs text-emerald-100 mb-3">{description}</p>
          
          <Button
            size="sm"
            onClick={handleComplete}
            className="w-full bg-white text-emerald-700 hover:bg-emerald-50 h-8 text-xs"
          >
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Got it!
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTooltip;
